import { createTagFilterRule } from "../../../companies/tags/filter_rules";
// convex/lib/config/domains/quotations.ts

import { validatorQuotation } from "../../../companies/quotations";
import { validatorQuotationItem } from "../../../companies/quotations/items";
import { validatorClient } from "../../../companies/clients";
import { validatorSeller } from "../../../companies/sales/sellers";
import { validatorProductService } from "../../../companies/products";
import { validatorUsers } from "../../../system/users";
import { quotationStatusRule } from "../../../companies/quotations/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// QUOTATIONS DOMAIN - Status Values for i18n
// ============================================================================

export const QUOTATION_STATUSES = [
  "draft",
  "sent",
  "accepted",
  "rejected",
  "expired",
] as const;

export const quotationsDomain: DomainGroup = {
  id: "quotations",
  name: "Cotizaciones",
  description: "Gestión de cotizaciones y presupuestos",
  icon: "FileText",
  order: 5,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "quotations",
    keys: {
      title: "quotations.title",
      description: "quotations.description",
    },
  },

  modules: [
    {
      id: "quotations",
      name: "Cotizaciones",
      description: "Creación y seguimiento de cotizaciones",
      icon: "ClipboardList",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/quotations"],
      navigation: {
        showInNav: true,
        contexts: ["main", "mobile"],
        navOrder: 12,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "quotations.quotations",
        keys: {
          title: "quotations.quotations.title",
          description: "quotations.quotations.description",
          longDescription: "quotations.quotations.longDescription",
          searchPlaceholder: "quotations.quotations.searchPlaceholder",
          emptyState: "quotations.quotations.emptyState",
          actions: {
            create: "quotations.quotations.actions.create",
            edit: "quotations.quotations.actions.edit",
            delete: "quotations.quotations.actions.delete",
            view: "quotations.quotations.actions.view",
            duplicate: "quotations.quotations.actions.duplicate",
          },
        },
        translatableFields: ["title", "description", "notes"],
        translatableEnums: [{ field: "status", values: QUOTATION_STATUSES }],
      },

      tables: [
        {
          tableName: "quotations",
          schema: validatorQuotation,
          scriptActions: ["accept", "reject", "expire"],
          scope: "branch" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "seller",
              schema: validatorSeller,
              tableName: "sellers",
            },
            {
              key: "client",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["name", "contactPerson"],
            },
          ],
          searchFields: ["code", "title", "description", "notes"],
          hasStatusIndex: false,
          customRules: [quotationStatusRule, createTagFilterRule("quotations")],
          withTags: true,
        },
        {
          tableName: "quotations_items",
          schema: validatorQuotationItem,
          scope: "company" as TableScope,
          relations: [
            {
              key: "quotation",
              schema: validatorQuotation,
              tableName: "quotations",
            },
            {
              key: "product",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["description"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "sellers" },
        { tableName: "products" },
      ],
    },
  ],
};
